import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaMapMarkerAlt, FaUsers, FaClock, FaCar, FaCarSide, FaWalking, FaTag } from 'react-icons/fa';
import { CustomButton2, CustomButton3, EventCardButton, EventCardDetail } from '../../Templates';
import '../styles/EventCard.css';

/**
 * Component to display an event card with details and actions
 * 
 * @param {Object} props
 * @param {Object} props.event - The event data object
 * @param {Function} props.onAttend - Function to call when attending/interested in event
 * @param {Function} props.onViewDetails - Function to call to view event details
 * @param {Function} props.onOfferRide - Function to call when offering a ride
 * @param {Function} props.onLookingForRide - Function to call when looking for a ride
 */
function EventCard({ event, onAttend, onViewDetails, onOfferRide, onLookingForRide }) {
  const { t } = useTranslation('common');
  
  // Format date 
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Format time in 24-hour format
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(undefined, { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false // Use 24-hour format
    });
  };

  // Truncate description to a reasonable length
  const truncateDescription = (text, maxLength = 120) => {
    if (!text) return t('events.no_description_available');
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const defaultBanner = 'none'; // Using 'none' to apply a solid color instead from CSS
  const bannerImageUrl = event.banner_image_url || defaultBanner;

  // Format location to show only city and country/municipality
  const formatLocation = (locationString) => {
    if (!locationString) return '';
    
    // Split the location by commas
    const parts = locationString.split(',');
    
    if (parts.length <= 1) {
      // If there's only one part, return it (just the city)
      return parts[0].trim();
    } else {
      // Otherwise return the city (first part)
      const cityPart = parts[0].trim();
      // Truncate if too long
      return cityPart.length > 20 ? cityPart.substring(0, 17) + '...' : cityPart;
    }
  };

  return (
    <div className={`event-card ${event.isAttending ? 'attending' : ''}`}>
      <div 
        className={`event-banner ${!event.banner_image_url ? 'empty-banner' : ''}`} 
        style={{ backgroundImage: bannerImageUrl !== 'none' ? `url(${bannerImageUrl})` : 'none' }}
        onClick={onViewDetails}
      >
        {event.isAttending && (
          <div className="attending-badge">
            {t('events.attending')}
          </div>
        )}
        
        {event.category && (
          <div className="event-category">
            <FaTag />
            {t(`events.categories.${event.category}`)}
          </div>
        )}
        
        <div className="event-date-badge">
          <FaCalendarAlt />
          {formatDate(event.start_date)}
        </div>
        
        {/* Display event name on the banner */}
        <div className="event-banner-title">
          {event.title || 'try'}
        </div>
      </div>
      
      <div className="event-content">
        <div className="event-details">
          <EventCardDetail 
            icon={<FaClock />} 
            text={formatTime(event.start_date) || t('posts.not_specified')} 
          />
          
          <EventCardDetail 
            icon={<FaMapMarkerAlt />} 
            text={formatLocation(event.location || t('events.location_not_specified'))} 
          />
          
          <EventCardDetail 
            icon={<FaUsers />} 
            text={`${event.attendeeCount || 0} ${t('events.attending_count')}`} 
          />
          
          {event.rides_count > 0 && (
            <EventCardDetail 
              icon={<FaCar />} 
              text={`${event.rides_count} ${t('events.available_rides')}`} 
            />
          )}
        </div>
        
        <div className="event-description-container">
          <p className="event-description">
            {truncateDescription(event.description)}
          </p>
        </div>
      </div>
      
      <div className="event-actions">
        <EventCardButton
          label={t('events.view_details') || 'VIEW DETAILS'}
          onClick={onViewDetails}
          className="view-details-button"
        />
      </div>
    </div>
  );
}

export default EventCard;